
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { handleWebhookNotification } from "@/utils/auth.utils";
import { AuthResult, SignOutResult } from "@/types/auth.types";

export const useAuthActions = () => {
  const { toast } = useToast();

  const signInWithEmail = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error('Login error details:', error);
        
        if (error.message === 'Invalid login credentials') {
          toast({
            title: "Inloggen mislukt",
            description: "E-mailadres of wachtwoord is niet correct. Controleer je gegevens en probeer het opnieuw.",
            variant: "destructive",
          });
        } else if (error.message.includes('Email not confirmed')) {
          toast({
            title: "E-mail niet bevestigd",
            description: "Je moet eerst je e-mailadres bevestigen via de link in je inbox.",
            variant: "destructive",
          });
        } else if (error.message.includes('Password should be at least 6 characters')) {
          toast({
            title: "Ongeldig wachtwoord",
            description: "Je wachtwoord moet minimaal 6 tekens lang zijn.",
            variant: "destructive",
          });
        } else if (error.message.includes('Invalid email')) {
          toast({
            title: "Ongeldig e-mailadres",
            description: "Voer een geldig e-mailadres in.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Fout bij inloggen",
            description: "Er is een onverwachte fout opgetreden. Probeer het later opnieuw.",
            variant: "destructive",
          });
        }
        throw error;
      }

      toast({
        title: "Succesvol ingelogd",
        description: "Welkom terug!",
      });
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string): Promise<AuthResult> => {
    try {
      if (password.length < 6) {
        toast({
          title: "Ongeldig wachtwoord",
          description: "Je wachtwoord moet minimaal 6 tekens lang zijn.",
          variant: "destructive",
        });
        return { error: new Error("Password should be at least 6 characters") };
      }

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) {
        console.error('Signup error details:', error);

        if (error.message.includes('User already registered')) {
          toast({
            title: "Registratie mislukt",
            description: "Dit e-mailadres is al geregistreerd",
            variant: "destructive",
          });
        } else if (error.message.includes('Password should be at least 6 characters')) {
          toast({
            title: "Ongeldig wachtwoord",
            description: "Je wachtwoord moet minimaal 6 tekens lang zijn.",
            variant: "destructive",
          });
        } else if (error.message.includes('Invalid email')) {
          toast({
            title: "Ongeldig e-mailadres",
            description: "Voer een geldig e-mailadres in.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Fout bij registreren",
            description: error.message,
            variant: "destructive",
          });
        }
        return { error };
      }

      try {
        await handleWebhookNotification(email);
        toast({
          title: "Registratie succesvol",
          description: "Je account is aangemaakt en de activatiemail is verzonden",
        });
      } catch (webhookError) {
        console.error('Webhook error:', webhookError);
        toast({
          title: "Registratie succesvol",
          description: "Je account is aangemaakt, maar er was een probleem met de webhook",
        });
      }

      return {};
    } catch (error: any) {
      console.error('Signup error:', error);
      return { error };
    }
  };

  const signOut = async (): Promise<SignOutResult> => {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        if (error.message.includes('session_not_found') || 
            error.message.includes('User from sub claim in JWT does not exist')) {
          return { success: true, needsUserReset: true };
        }
        throw error;
      }

      return { success: true, needsUserReset: true };
    } catch (error: any) {
      console.error('Signout error:', error);
      
      if ((error.status === 403 && error.message.includes('session_not_found')) ||
          error.message.includes('User from sub claim in JWT does not exist')) {
        return { success: true, needsUserReset: true };
      }
      
      return { success: false, error };
    }
  };

  const resetPassword = async (email: string) => {
    console.log('Starting password reset process for', email);
    // Change redirectTo to dashboard and add type=recovery for explicit recovery tracking
    return await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback?type=recovery&destination=dashboard`,
    });
  };

  return {
    signInWithEmail,
    signUp,
    signOut,
    resetPassword,
  };
};
